import React, { useState, useEffect } from 'react';
import { Check, Clock, AlertCircle, HardDrive } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AutoSaveIndicatorProps {
  /** Whether auto-save is enabled */
  enabled?: boolean;
  /** Last auto-save timestamp */
  lastSaved?: string | null;
  /** Whether there are unsaved changes */
  hasUnsavedChanges?: boolean;
  /** Whether content was ever saved to cloud */
  hasCloudSave?: boolean;
  /** Whether content exists (not empty) */
  hasContent?: boolean;
  /** Whether user is currently typing */
  isTyping?: boolean;
  /** Custom className */
  className?: string;
  /** Show text label */
  showLabel?: boolean;
}

/**
 * Component to show auto-save status
 */
export function AutoSaveIndicator({
  enabled = true,
  lastSaved,
  hasUnsavedChanges = false,
  hasCloudSave = false,
  hasContent = false,
  isTyping = false,
  className,
  showLabel = true
}: AutoSaveIndicatorProps) {
  const [timeAgo, setTimeAgo] = useState<string>('');

  // Update time ago display
  useEffect(() => {
    if (!lastSaved) return;

    const updateTimeAgo = () => {
      const now = new Date();
      const savedTime = new Date(lastSaved);
      const diffMs = now.getTime() - savedTime.getTime();
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);

      if (diffSeconds < 60) {
        setTimeAgo('just now');
      } else if (diffMinutes < 60) {
        setTimeAgo(`${diffMinutes}m ago`);
      } else if (diffHours < 24) {
        setTimeAgo(`${diffHours}h ago`);
      } else {
        setTimeAgo(savedTime.toLocaleDateString());
      }
    };

    updateTimeAgo();
    const interval = setInterval(updateTimeAgo, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [lastSaved]);

  if (!enabled || !hasContent) {
    return null;
  }

  const getIcon = () => {
    // Simple two-state system
    if (lastSaved) {
      return <HardDrive className="h-3 w-3" />;
    }
    if (hasCloudSave && !lastSaved) {
      return <Check className="h-3 w-3" />;
    }
    return null;
  };

  const getStatus = () => {
    // Simple two-state system
    if (lastSaved) {
      return 'Draft saved locally';
    }
    if (hasCloudSave && !lastSaved) {
      return 'All changes saved';
    }
    return null;
  };

  const getStatusColor = () => {
    if (lastSaved) {
      return 'text-muted-foreground'; // Match word count styling
    }
    if (hasCloudSave && !lastSaved) {
      return 'text-green-600 dark:text-green-400'; // Cloud saved
    }
    return 'text-muted-foreground';
  };

  const status = getStatus();
  const icon = getIcon();

  // Only show indicator when there's something meaningful to communicate
  if (!status && !icon) {
    return null;
  }

  return (
    <div className={cn(
      'flex items-center gap-1.5 text-xs transition-opacity duration-200',
      getStatusColor(),
      className
    )}>
      {icon}
      {showLabel && status && (
        <span className="font-medium">
          {status}
        </span>
      )}
    </div>
  );
}

/**
 * Reactive Auto-Save Indicator Component
 * This component automatically updates when auto-save data changes
 */
export function ReactiveAutoSaveIndicator({
  autoSaveData,
  className,
  showLabel = true
}: {
  autoSaveData: {
    getAutoSavedContent: () => any;
    hasUnsavedContent: () => boolean;
    hasCloudSave: () => boolean;
    hasContent: () => boolean;
    isTyping: () => boolean;
  };
  className?: string;
  showLabel?: boolean;
}) {
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasCloudSave, setHasCloudSave] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    const updateStatus = () => {
      const autoSaved = autoSaveData.getAutoSavedContent();
      const hasUnsaved = autoSaveData.hasUnsavedContent();
      const hasCloud = autoSaveData.hasCloudSave();
      const hasContentValue = autoSaveData.hasContent();
      const isTypingValue = autoSaveData.isTyping();

      setLastSaved(autoSaved?.timestamp || null);
      setHasUnsavedChanges(hasUnsaved);
      setHasCloudSave(hasCloud);
      setHasContent(hasContentValue);
      setIsTyping(isTypingValue);
    };

    updateStatus();

    // Update status less frequently since we're being more subtle
    const interval = setInterval(updateStatus, 1000);

    return () => clearInterval(interval);
  }, [autoSaveData]);

  return (
    <AutoSaveIndicator
      lastSaved={lastSaved}
      hasUnsavedChanges={hasUnsavedChanges}
      hasCloudSave={hasCloudSave}
      hasContent={hasContent}
      isTyping={isTyping}
      className={className}
      showLabel={showLabel}
    />
  );
}

/**
 * Hook to manage auto-save indicator state
 */
export function useAutoSaveIndicator(autoSaveData: {
  getAutoSavedContent: () => any;
  hasUnsavedContent: () => boolean;
}) {
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    const updateStatus = () => {
      const autoSaved = autoSaveData.getAutoSavedContent();
      const hasUnsaved = autoSaveData.hasUnsavedContent();

      setLastSaved(autoSaved?.timestamp || null);
      setHasUnsavedChanges(hasUnsaved);
    };

    updateStatus();

    // Update status periodically
    const interval = setInterval(updateStatus, 1000);

    return () => clearInterval(interval);
  }, [autoSaveData]);

  return {
    lastSaved,
    hasUnsavedChanges
  };
}
